import React, { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

interface LoginNotificationModalProps {
  open: boolean;
  onClose: () => void;
  refinanceEnabled: boolean;
  isInvestorMode: boolean;
}

function describeWorkflows(
  refinanceEnabled: boolean,
  isInvestorMode: boolean
): { headline: string; detail: string; tip: string } {
  const extras: string[] = [];
  if (refinanceEnabled) extras.push('refinance tools');
  if (isInvestorMode) extras.push('investor loan programs');

  const headline =
    extras.length === 0 ? 'Purchase Mode' : `Purchase + ${extras.join(' + ')}`;

  const detail =
    extras.length === 0
      ? "You're set up for home buying with property specific loan estimates."
      : `You still have full purchase workflows; ${extras.join(
          ' and '
        )} are turned on in addition.`;

  const tip =
    extras.length === 0
      ? 'Visit Profile → Loan workflows to enable refinance tools or investor programs anytime—they add options without removing purchase.'
      : 'You can adjust refinance and investor toggles on your profile anytime. Purchase stays your base experience.';

  return { headline, detail, tip };
}

export const LoginNotificationModal = ({
  open,
  onClose,
  refinanceEnabled,
  isInvestorMode,
}: LoginNotificationModalProps) => {
  const theme = useTheme();

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        onClose();
      }, 50000);

      return () => clearTimeout(timer);
    }
  }, [open, onClose]);

  const { headline, detail, tip } = describeWorkflows(
    refinanceEnabled,
    isInvestorMode
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.mainColors.background,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ color: theme.palette.text.primary, mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            Welcome to Unbeatable!
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body1" sx={{ mb: 2, color: 'black' }}>
          You&apos;re in <strong>{headline}</strong>. {detail}
        </Typography>

        <Box
          sx={{
            p: 2,
            background: alpha('#0a1f44', 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha('#0a1f44', 0.1)}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 500, color: 'black' }}
          >
            Pro tip:
          </Typography>
          <Typography variant="body2" color={theme.palette.text.primary}>
            {tip}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ minWidth: 100 }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};
